import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  AdminPanelSettings as AdminIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  GetApp as DownloadIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import axiosClient from "../features/axiosInstance";
import { useAccessControl } from "../context/AccessControlContext";

function AdminActions() {
  const { isAdmin, isLoading } = useAccessControl();
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null,
    dryRun: false,
  });
  const [taskDialog, setTaskDialog] = useState({
    open: false,
    jobId: null,
    task: null,
  });

  // Wait for role check to complete
  if (isLoading) {
    return (
      <Box sx={{ py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          Access Denied: Only administrators can access this page.
        </Alert>
      </Box>
    );
  }

  const handleStartRecalculation = async (dryRun) => {
    try {
      setError(null);
      const response = await axiosClient.post("/admin/recalculation/start", {
        dryRun,
      });

      const jobId = response.data.task.id;

      // Open task dialog and start polling
      setTaskDialog({
        open: true,
        jobId,
        task: response.data.task,
      });

      setConfirmDialog({ open: false, action: null, dryRun: false });
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleConfirmAction = (action, dryRun = false) => {
    setConfirmDialog({
      open: true,
      action,
      dryRun,
    });
  };

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <AdminIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5">Admin Actions</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Points Recalculation Section */}
        <Card sx={{ borderLeft: "4px solid #ff9800" }}>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Recalculate Player Points
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Recalculate points for all entries based on current tier,
                format, position, and category allocations. This process runs in
                the background.
              </Typography>

              <Paper
                sx={{
                  bgcolor: "#fff3e0",
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                  border: "1px solid #ffb74d",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ display: "block", mb: 1, color: "#333" }}
                >
                  <strong>Dry Run:</strong> Preview changes without applying
                  them
                </Typography>
                <Typography variant="body2" sx={{ color: "#333" }}>
                  <strong>Execute:</strong> Apply recalculation to the database
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => handleConfirmAction("dryRun", true)}
                sx={{ flex: 1 }}
              >
                Start Dry Run
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<WarningIcon />}
                onClick={() => handleConfirmAction("execute", false)}
                sx={{ flex: 1 }}
              >
                Execute
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, action: null, dryRun: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.dryRun ? "Start Dry Run?" : "Execute Recalculation?"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {confirmDialog.dryRun ? (
              <>
                <Typography>
                  This will calculate what the new points would be without
                  making any database changes.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  You'll be able to review the changes before executing.
                </Typography>
              </>
            ) : (
              <>
                <Alert severity="warning">
                  This will update all entry points in the database based on
                  current allocations.
                </Alert>
                <Typography>
                  Make sure you've run a dry run first to review the changes.
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, action: null, dryRun: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleStartRecalculation(confirmDialog.dryRun)}
            variant="contained"
            color={confirmDialog.dryRun ? "primary" : "warning"}
          >
            {confirmDialog.dryRun ? "Start Dry Run" : "Execute"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Progress Dialog */}
      {taskDialog.open && (
        <TaskProgressDialog
          jobId={taskDialog.jobId}
          initialTask={taskDialog.task}
          onClose={() =>
            setTaskDialog({ open: false, jobId: null, task: null })
          }
        />
      )}
    </Box>
  );
}

/**
 * Task Progress Dialog Component
 * Polls job status and shows progress
 */
function TaskProgressDialog({ jobId, initialTask, onClose }) {
  const [task, setTask] = useState(initialTask);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const changes = Array.isArray(task.data?.changes) ? task.data.changes : [];
  const pageCount = Math.max(1, Math.ceil(changes.length / PAGE_SIZE));
  const pageChanges = changes.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  // Poll job status
  useEffect(() => {
    if (!jobId) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axiosClient.get(`/admin/recalculation/${jobId}`);
        setTask(response.data.task);

        // Stop polling if job is done
        if (
          response.data.task.state === "completed" ||
          response.data.task.state === "failed"
        ) {
          clearInterval(pollInterval);
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [jobId]);

  const handleCancel = async () => {
    try {
      setLoading(true);
      await axiosClient.post(`/admin/recalculation/${jobId}/cancel`);
      setTask({ ...task, state: "cancelled" });
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadErrors = () => {
    const rows = Array.isArray(task.data?.errorDetails)
      ? task.data.errorDetails
      : [];
    if (rows.length === 0) return;

    const escapeCsv = (value) => {
      const text = String(value ?? "");
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };

    // Collect every field present in the uploaded records so the CSV carries
    // the full row, not just the summary.
    const detailKeys = [];
    for (const row of rows) {
      if (row.details && typeof row.details === "object") {
        for (const key of Object.keys(row.details)) {
          if (!detailKeys.includes(key)) detailKeys.push(key);
        }
      }
    }

    const csv = [
      ["Entry ID", "Player", "Event", "Error", ...detailKeys].join(","),
      ...rows.map((row) =>
        [
          row.entryId,
          row.player_name,
          row.event_name,
          row.error,
          ...detailKeys.map((key) => row.details?.[key]),
        ]
          .map(escapeCsv)
          .join(","),
      ),
    ].join("\n");

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csv),
    );
    element.setAttribute("download", `recalculate_errors_${Date.now()}.csv`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDownload = (csvKey) => {
    if (!task.data?.csvFiles || !task.data.csvFiles[csvKey]) return;

    const csvData = task.data.csvFiles[csvKey];
    if (
      !csvData ||
      typeof csvData.content !== "string" ||
      typeof csvData.filename !== "string"
    )
      return;

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csvData.content)
    );
    element.setAttribute("download", csvData.filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const isInProgress = task.state === "active" || task.state === "pending";
  const isCompleted = task.state === "completed";
  const isFailed = task.state === "failed";

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isInProgress && <CircularProgress size={24} />}
            {isCompleted && <CheckCircleIcon sx={{ color: "success.main" }} />}
            {isFailed && <CloseIcon sx={{ color: "error.main" }} />}
            <Typography variant="h6">
              {String(task.isDryRun) === "true" ? "Dry Run" : "Execution"} -{" "}
              {String(task.state || "unknown")}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Progress Bar */}
          {isInProgress && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2">Progress</Typography>
                <Typography variant="body2">{task.status || 0}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={task.status || 0} />
            </Box>
          )}

          {/* Statistics */}
          {task.data && task.data.total !== undefined && (
            <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">Total Entries:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {task.data.total || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">Updated:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {task.data.updated || 0}
                  </Typography>
                </Box>
                {task.data.errors > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="error">
                      Errors:
                    </Typography>
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ fontWeight: 600 }}
                    >
                      {task.data.errors}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Changed entries - small paginated table */}
          {isCompleted && changes.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Entries Updated ({changes.length})
              </Typography>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entry</TableCell>
                      <TableCell>Player</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell align="right">Old</TableCell>
                      <TableCell align="right">New</TableCell>
                      <TableCell align="right">Diff</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageChanges.map((change) => (
                      <TableRow key={change.entryId}>
                        <TableCell>{change.entryId}</TableCell>
                        <TableCell>{change.player_name}</TableCell>
                        <TableCell>{change.event_name}</TableCell>
                        <TableCell align="right">{change.oldPoints}</TableCell>
                        <TableCell align="right">{change.newPoints}</TableCell>
                        <TableCell align="right">
                          {Number(change.newPoints - change.oldPoints).toFixed(2)}
                        </TableCell>
                        <TableCell>{change.reason || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Button
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Typography variant="caption">
                  Page {page} of {pageCount}
                </Typography>
                <Button
                  size="small"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {/* Download CSVs */}
          {isCompleted &&
            (task.data?.csvFiles?.entries || task.data?.csvFiles?.rankings) && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Download Results
                </Typography>
                <Stack spacing={1}>
                  {task.data.csvFiles?.entries && (
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload("entries")}
                      fullWidth
                      size="small"
                    >
                      Download Entry Changes
                    </Button>
                  )}
                  {task.data.csvFiles?.rankings && (
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload("rankings")}
                      fullWidth
                      size="small"
                    >
                      Download Rankings
                    </Button>
                  )}
                  {isCompleted &&
                    Array.isArray(task.data?.errorDetails) &&
                    task.data.errorDetails.length > 0 && (
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadErrors}
                        fullWidth
                        size="small"
                      >
                        Download Errored Entries (
                        {task.data.errorDetails.length})
                      </Button>
                    )}
                </Stack>
              </Box>
            )}

          {/* Error Details */}
          {isCompleted &&
            task.data?.errorDetails &&
            Array.isArray(task.data.errorDetails) &&
            task.data.errorDetails.length > 0 && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1, color: "error.main" }}
                >
                  Failed Entries ({task.data.errorDetails.length})
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "#ffebee",
                    maxHeight: 300,
                    overflow: "auto",
                  }}
                >
                  <Stack spacing={1}>
                    {task.data.errorDetails.map((error, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        sx={{ color: "#c62828" }}
                      >
                        Entry {error?.entryId}:{" "}
                        {String(error?.error || "Unknown error")}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              </Box>
            )}

          {/* Error Message */}
          {isFailed && (
            <Alert severity="error">
              Task failed. Please check the logs for more details.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        {isInProgress && (
          <Button onClick={handleCancel} disabled={loading} color="error">
            Cancel Task
          </Button>
        )}
        <Button onClick={onClose}>{isInProgress ? "Minimize" : "Close"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AdminActions;
